(() => {
  'use strict';
  if (window.CartDebug) return;
  const key = 'theme:cart-debug';
  const bufferKey = `${key}:events`;
  const version = 'v4-v16-atc-backport-1';
  const selector = '[name="add"], [id^="SectionAtcBtn-"], .sticky-atc__add_button';
  let enabled = false;
  let events = [];
  let sequence = 0;
  const attempts = new WeakMap();
  try {
    const option = new URLSearchParams(location.search).get('cart_debug');
    if (option === '1' || option === '0') sessionStorage.setItem(key, option);
    enabled = sessionStorage.getItem(key) === '1';
    if (enabled) events = JSON.parse(sessionStorage.getItem(bufferKey) || '[]').slice(-250);
    else sessionStorage.removeItem(bufferKey);
  } catch (_) {
    enabled = new URLSearchParams(location.search).get('cart_debug') === '1';
    events = [];
  }
  const describeButton = (button) => button ? {
    id: button.id, type: button.type, formId: button.form?.getAttribute('id'),
    disabled: button.disabled, ariaDisabled: button.getAttribute('aria-disabled'),
    loading: button.classList.contains('loading'), connected: button.isConnected,
  } : null;
  const log = (stage, data = {}) => {
    if (!enabled) return;
    // Callers pass diagnostic metadata only: no cart tokens, properties,
    // customer fields, response bodies, query strings, or cookies.
    try {
      const entry = { time: new Date().toISOString(), ...data, version, stage };
      events.push(entry);
      if (events.length > 250) events.shift();
      try { sessionStorage.setItem(bufferKey, JSON.stringify(events)); } catch (_) { /* Storage is optional. */ }
      console.info(`[CartDebug] ${stage}`, entry);
    } catch (_) { /* Diagnostics must never interrupt a purchase. */ }
  };
  const owner = (button) => button?.form?.closest('product-form')
    || button?.closest('product-form')
    || document.querySelector('product-form[data-main="true"]');
  const snapshot = () => ({
    version, enabled, online: navigator.onLine, visibility: document.visibilityState,
    viewport: { width: window.innerWidth, height: window.innerHeight, touchPoints: navigator.maxTouchPoints || 0 },
    registered: ['product-form', 'sticky-atc', 'cart-drawer', 'variant-selects'].map((name) => ({ name, ready: !!customElements.get(name) })),
    forms: Array.from(document.querySelectorAll('product-form')).map((form) => ({
      id: form.id, section: form.dataset.section, bound: typeof form.handleSubmit === 'function',
      submitting: !!(form.isSubmitting || form.submitting), variantId: form.querySelector('[name="id"]')?.value,
      subscriptionPending: form.closest('product-info')?.querySelector('subscription-widget')?.dataset.variantPending,
      buttons: Array.from(form.querySelectorAll(selector)).map(describeButton),
    })),
  });
  window.CartDebug = {
    log, snapshot,
    get enabled() { return enabled; },
    start(form, button) {
      if (!enabled) return undefined;
      const traceId = `atc-${Date.now().toString(36)}-${++sequence}`;
      attempts.set(form, traceId);
      log('submit.received', { traceId, section: form.dataset.section, button: describeButton(button) });
      return traceId;
    },
    summarizeForm(formData) {
      if (!enabled) return undefined;
      return Array.from(formData.entries()).filter(([name]) => /^(id|quantity|selling_plan|items\[\d+\]\[(id|quantity|selling_plan)\])$/.test(name))
        .map(([name, value]) => ({ name, value: /^\d+$/.test(String(value)) ? String(value) : '[invalid-or-empty]' }));
    },
    enable() {
      enabled = true;
      try { sessionStorage.setItem(key, '1'); } catch (_) {}
      log('debug.enabled', snapshot());
    },
    disable() {
      enabled = false;
      events = [];
      try { sessionStorage.setItem(key, '0'); sessionStorage.removeItem(bufferKey); } catch (_) {}
    },
    clear() { events = []; try { sessionStorage.removeItem(bufferKey); } catch (_) {} },
    export() { return JSON.stringify({ snapshot: snapshot(), events }, null, 2); },
    download() {
      const url = URL.createObjectURL(new Blob([this.export()], { type: 'application/json' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `cart-debug-${Date.now()}.json`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
  };
  document.addEventListener('pointerdown', (event) => {
    if (!enabled) return;
    const button = event.target.closest?.(selector);
    if (button) log('ui.pointer', { pointerType: event.pointerType, button: describeButton(button) });
    else {
      const covered = Array.from(document.querySelectorAll(selector)).find((item) => {
        const rect = item.getBoundingClientRect();
        return rect.width && rect.height && event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      });
      if (covered) log('ui.possible-overlay', { button: describeButton(covered), targetTag: event.target.tagName });
    }
  }, true);
  document.addEventListener('click', (event) => {
    if (!enabled) return;
    const button = event.target.closest?.(selector);
    if (!button) return;
    const form = owner(button);
    const previous = form && attempts.get(form);
    log('ui.click', { button: describeButton(button), formReady: typeof form?.handleSubmit === 'function' });
    if (button.closest('sticky-atc')?.dataset.scrollBtn === 'true') return;
    setTimeout(() => {
      if (!form || attempts.get(form) === previous) log('ui.no-submit', {
        button: describeButton(button), defaultPrevented: event.defaultPrevented,
        formReady: typeof form?.handleSubmit === 'function',
      });
    }, 1200);
  }, true);
  ['pointerup', 'pointercancel', 'touchstart', 'touchend', 'touchcancel'].forEach((type) => {
    document.addEventListener(type, (event) => {
      if (!enabled) return;
      const button = event.target.closest?.(selector);
      if (button) log(`ui.${type}`, { pointerType: event.pointerType, button: describeButton(button) });
    }, { capture: true, passive: true });
  });
  ['online', 'offline', 'pageshow', 'pagehide'].forEach((type) => window.addEventListener(type, (event) => {
    log(`page.${type}`, { persisted: event.persisted, online: navigator.onLine, visibility: document.visibilityState });
  }));
  document.addEventListener('visibilitychange', () => log('page.visibility', { visibility: document.visibilityState }));
  // Query-enabled, isolated UI lets phone users export without a developer console.
  const mountPanel = () => {
    if (!enabled || !document.body || document.getElementById('cart-debug-panel')) return;
    const host = document.createElement('div');
    host.id = 'cart-debug-panel';
    host.style.cssText = 'display:block;position:fixed;bottom:4px;left:4px;z-index:2147483647';
    const shadow = host.attachShadow({ mode: 'open' });
    shadow.innerHTML = '<style>details{font:12px sans-serif;background:#fff;color:#111;border:1px solid #777;padding:6px;max-width:260px}button{margin:4px;padding:8px}textarea{width:240px;height:160px}</style><details><summary>Cart debug · V4.1</summary><button type="button" data-export>Export logs</button><button type="button" data-view>Show logs</button><button type="button" data-off>Turn off debugging</button><textarea aria-label="Debug logs" hidden readonly></textarea></details>';
    shadow.querySelector('[data-export]').onclick = () => { try { window.CartDebug.download(); } catch (_) { shadow.querySelector('[data-view]').click(); } };
    shadow.querySelector('[data-view]').onclick = () => { const area = shadow.querySelector('textarea'); area.hidden = false; area.value = window.CartDebug.export(); area.select(); };
    shadow.querySelector('[data-off]').onclick = () => { window.CartDebug.disable(); host.remove(); };
    document.body.append(host);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountPanel, { once: true });
  else mountPanel();
  document.addEventListener('invalid', (event) => {
    if (enabled && event.target.closest('product-form')) log('ui.validation-blocked', { fieldType: event.target.type });
  }, true);
  window.addEventListener('error', (event) => {
    if (!enabled) return;
    const source = event.filename || event.target?.src;
    let file;
    try { file = source ? new URL(source, location.href).pathname : undefined; } catch (_) {}
    log('runtime.error', { name: event.error?.name, file, line: event.lineno, resourceTag: event.target?.tagName });
  }, true);
  window.addEventListener('unhandledrejection', (event) => log('runtime.unhandled-rejection', { name: event.reason?.name, code: event.reason?.code }));
  document.addEventListener('cart-drawer:open', () => log('drawer.open'));
  document.addEventListener('shopify:section:load', () => log('editor.section-load', snapshot()));
  log('debug.ready', snapshot());
})();
